/**
 * Vector2 - 2D Vector Class
 *
 * Represents a point or direction in 2D space.
 * Used for screen positions, UI elements, and mouse input.
 *
 * YOUR TASK: Implement all the methods marked with TODO
 *
 * TypeScript Concepts Demonstrated:
 * - Classes with constructors
 * - Public properties
 * - Static methods (class-level methods)
 * - Return types
 * - Default parameter values
 * - Readonly constants
 *
 * @example
 * const position = new Vector2(100, 200);
 * const direction = Vector2.right();
 * const newPosition = position.add(direction.multiply(5));
 */
export class Vector2 {
  /**
   * Epsilon for floating-point comparisons
   * Two floats are "equal" if their difference is less than this
   */
  private static readonly EPSILON = 0.0001;

  /**
   * The X component of the vector
   */
  public x: number;

  /**
   * The Y component of the vector
   */
  public y: number;

  /**
   * Create a new Vector2
   * @param x - The X component (default: 0)
   * @param y - The Y component (default: 0)
   */
  constructor(x: number = 0, y: number = 0) {
    this.x = x;
    this.y = y;
  }

  // ============================================
  // STATIC FACTORY METHODS
  // These create common vectors without using 'new'
  // ============================================

  /**
   * Create a zero vector (0, 0)
   */
  static zero(): Vector2 {
    return new Vector2(0, 0);
  }

  /**
   * Create a one vector (1, 1)
   */
  static one(): Vector2 {
    // TODO: Implement this method
    return new Vector2(1, 1);
  }

  /**
   * Create an up vector (0, -1)
   * Note: In screen coordinates, Y increases downward, so "up" is negative Y
   */
  static up(): Vector2 {
    // TODO: Implement this method
    return new Vector2(0, -1);
  }

  /**
   * Create a down vector (0, 1)
   */
  static down(): Vector2 {
    // TODO: Implement this method
    return new Vector2(0, 1);
  }

  /**
   * Create a left vector (-1, 0)
   */
  static left(): Vector2 {
    // TODO: Implement this method
    return new Vector2(-1, 0);
  }

  /**
   * Create a right vector (1, 0)
   */
  static right(): Vector2 {
    // TODO: Implement this method
    return new Vector2(1, 0);
  }

  // ============================================
  // ARITHMETIC METHODS
  // All arithmetic should return NEW vectors (immutable pattern)
  // ============================================

  /**
   * Add another vector to this one
   * @param other - The vector to add
   * @returns A new vector representing the sum
   */
  add(other: Vector2): Vector2 {
    // TODO: Implement this method
    // Remember: Return a NEW vector, don't modify 'this'
    return new Vector2(this.x + other.x, this.y + other.y);
  }

  /**
   * Subtract another vector from this one
   * @param other - The vector to subtract
   * @returns A new vector representing the difference
   */
  subtract(other: Vector2): Vector2 {
    // TODO: Implement this method
    return new Vector2(this.x - other.x, this.y - other.y);
  }

  /**
   * Multiply this vector by a scalar
   * @param scalar - The number to multiply by
   * @returns A new scaled vector
   */
  multiply(scalar: number): Vector2 {
    // TODO: Implement this method
    return new Vector2(this.x * scalar, this.y * scalar);
  }

  /**
   * Divide this vector by a scalar
   * @param scalar - The number to divide by
   * @returns A new divided vector (returns clone if scalar is 0)
   */
  divide(scalar: number): Vector2 {
    // TODO: Implement this method
    // Handle division by zero!
    if (scalar === 0) {
      console.warn('Vector2.divide: Division by zero, returning clone');
      return this.clone();
    }
    return new Vector2(this.x / scalar, this.y / scalar);
  }

  // ============================================
  // VECTOR OPERATIONS
  // ============================================

  /**
   * Calculate the dot product with another vector
   * The dot product tells you how "aligned" two vectors are:
   * - Positive: pointing in similar direction
   * - Zero: perpendicular
   * - Negative: pointing in opposite directions
   *
   * @param other - The other vector
   * @returns The dot product (a scalar)
   */
  dot(other: Vector2): number {
    // TODO: Implement this method
    // Formula: x1*x2 + y1*y2
    return this.x * other.x + this.y * other.y;
  }

  /**
   * Calculate the magnitude (length) of this vector
   * @returns The length of the vector
   */
  magnitude(): number {
    // TODO: Implement this method
    // Formula: sqrt(x² + y²)
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  /**
   * Calculate the squared magnitude
   * This is faster than magnitude() because it avoids the square root
   * Useful for comparing distances without needing exact values
   *
   * @returns The squared length of the vector
   */
  magnitudeSquared(): number {
    // TODO: Implement this method
    return this.x * this.x + this.y * this.y;
  }

  /**
   * Get a normalized (unit) vector with magnitude 1
   * @returns A new unit vector in the same direction
   */
  normalize(): Vector2 {
    // TODO: Implement this method
    // Handle zero-length vectors!
    const mag = this.magnitude();
    if (mag === 0) {
      return Vector2.zero();
    }
    return this.divide(mag);
  }

  /**
   * Calculate distance to another point
   * @param other - The other point
   * @returns The distance between the two points
   */
  distance(other: Vector2): number {
    // TODO: Implement this method
    // Hint: Distance is the magnitude of (this - other)
    return this.subtract(other).magnitude();
  }

  /**
   * Calculate squared distance to another point
   * Faster than distance() because it avoids square root
   *
   * @param other - The other point
   * @returns The squared distance
   */
  distanceSquared(other: Vector2): number {
    // TODO: Implement this method
    return this.subtract(other).magnitudeSquared();
  }

  /**
   * Linear interpolation between this vector and another
   *
   * @param other - The target vector
   * @param t - Interpolation factor (0 = this, 1 = other)
   * @returns A new interpolated vector
   *
   * @example
   * const start = new Vector2(0, 0);
   * const end = new Vector2(10, 10);
   * start.lerp(end, 0.5); // Returns (5, 5)
   */
  lerp(other: Vector2, t: number): Vector2 {
    // TODO: Implement this method
    // Formula: this + (other - this) * t
    // Or equivalently: this * (1 - t) + other * t
    return new Vector2(
      this.x + (other.x - this.x) * t,
      this.y + (other.y - this.y) * t
    );
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Create a copy of this vector
   * @returns A new vector with the same values
   */
  clone(): Vector2 {
    // TODO: Implement this method
    return new Vector2(this.x, this.y);
  }

  /**
   * Check if this vector equals another (within floating-point tolerance)
   * @param other - The vector to compare with
   * @returns True if the vectors are approximately equal
   */
  equals(other: Vector2): boolean {
    // TODO: Implement this method
    // Use EPSILON for floating-point comparison!
    return (
      Math.abs(this.x - other.x) < Vector2.EPSILON &&
      Math.abs(this.y - other.y) < Vector2.EPSILON
    );
  }

  /**
   * Convert to string for debugging
   * @returns String in format "(x, y)"
   */
  toString(): string {
    // TODO: Implement this method
    return `(${this.x}, ${this.y})`;
  }

  // ============================================
  // BONUS METHODS (implement if you want extra practice)
  // ============================================

  /**
   * Rotate this vector by an angle
   * @param angle - Angle in radians
   * @returns A new rotated vector
   */
  rotate(angle: number): Vector2 {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Vector2(
      this.x * cos - this.y * sin,
      this.x * sin + this.y * cos
    );
  }

  /**
   * Get the angle of this vector in radians
   * @returns Angle in radians from positive X axis
   */
  angle(): number {
    return Math.atan2(this.y, this.x);
  }

  /**
   * Get perpendicular vector (rotated 90 degrees counter-clockwise)
   * @returns A new perpendicular vector
   */
  perpendicular(): Vector2 {
    return new Vector2(-this.y, this.x);
  }

  /**
   * Reflect this vector off a surface with given normal
   * @param normal - The surface normal (should be normalized)
   * @returns The reflected vector
   */
  reflect(normal: Vector2): Vector2 {
    // Formula: v - 2 * (v · n) * n
    const dot = this.dot(normal);
    return this.subtract(normal.multiply(2 * dot));
  }
}
